import React, { useState, useMemo, useEffect } from 'react';
import { Collapse } from 'antd';
import { IntroDetailProps } from './IntroArticle.types';
import styles from './IntroArticle.module.scss';
import classNames from 'classnames';
import NextImage from 'next/image';
import Image from 'next/image';
import { useTranslation } from '@/hooks/use-translation';
import dayJs from 'dayjs';
import placeholder from '@/public/img/PlaceholerImage_PC.png';
import RichText from '@/components/common/rich-text';
import { useLanguage } from '@/hooks/use-language';
import { handleRichText } from '@/utils/handleRichText';
import { jsonLdScriptProps } from 'react-schemaorg';
import Head from 'next/head';
import { FAQPage, BlogPosting, VideoObject } from 'schema-dts';
import { YOUTUBE_KEY, YOUTUBE_URL } from '@/constants/setting';
import { useHeader } from '@/hooks/use-header';
import * as cheerio from 'cheerio';
import { usePathname, useSearchParams } from 'next/navigation';

const IntroDetail: React.FC<IntroDetailProps> = props => {
  const { data: articleData, ratingScore } = props;
  const [collActiveKeys, setCollActiveKeys] = useState<string[]>([]);
  const [remarkCollActiveKey, setRemarkCollActiveKey] = useState<string[]>([]);
  const [originUrl, setOriginUrl] = useState('');
  const [schemaVideoArr, setSchemaVideoArr] = useState<any>([]);
  const language = useLanguage();
  const t = useTranslation();
  const headerData = useHeader();

  // todo refactor- video 2024年2月5
  // ：video params
  const [pageContent, setPageContent] = useState({});
  const copyPageData = JSON.parse(JSON.stringify(articleData));
  const contentData = copyPageData.modular_blocks?.filter((item: any) => item.rich_text_editor) || [];

  // get remarks Data and Q&A Data from articleData
  const { remarkList, questionList } = useMemo(() => {
    const formatObj = { remarkList: [], questionList: [] } as {
      remarkList: string[];
      questionList: { title: string; answer: string }[];
    };
    articleData?.modular_blocks?.forEach(item => {
      // remarkList
      if (item.remarks && item.remarks.remark_content) {
        formatObj.remarkList.push(item.remarks.remark_content);
      }
      // questionList
      if (item.faq_section && item.faq_section.question_and_answer) {
        const tempQuestionList = item.faq_section.question_and_answer.map(item => {
          if (item.faq_reference && item.faq_reference.length > 0) {
            return {
              title: item.faq_reference[0].title,
              answer: item.faq_reference[0].answer,
            };
          } else {
            return {
              title: item.question,
              answer: item.answer,
            };
          }
        });
        formatObj.questionList.push(...tempQuestionList);
      }
    });
    return {
      remarkList: formatObj.remarkList || [],
      questionList: formatObj.questionList || [],
    };
  }, [articleData]);

  // expand question item
  const handleQuestionItem = (res: any) => {
    if (res.length != 0) {
      let index = Number(res[1]);
      window?.dataLayer?.push({
        event: 'buttonClick',
        buttonTitle: 'faq',
        description: questionList[index]?.title,
        pageURL: window.location.href,
        componentName: 'article faq',
      });
    }
    setCollActiveKeys(res);
  };

  // init video
  const genVideoInfo = async () => {
    const parser = new DOMParser();
    const serializer = new XMLSerializer();

    //先清空
    setSchemaVideoArr([]);

    for (let content of contentData) {
      if (!content?.rich_text_editor?.rich_text_editor) return;
      const doc = parser.parseFromString(content?.rich_text_editor?.rich_text_editor, 'text/html');

      for (let i = 0; i < doc.getElementsByTagName('abc').length; i++) {
        const videoUrl = doc.getElementsByTagName('abc')[i]?.attributes[0]?.value;

        let VIDEO_ID =
          videoUrl.split('?').length > 1
            ? videoUrl.split('?')[1].split('=').length > 1
              ? videoUrl.split('?')[1].split('=')[1]
              : ''
            : '';

        if (!VIDEO_ID) {
          const tempUrl = videoUrl?.replace('https://www.youtube.com/embed/', '');
          VIDEO_ID = tempUrl?.substring(0, tempUrl?.indexOf('?') != -1 ? tempUrl?.indexOf('?') : tempUrl.length);

          if (!VIDEO_ID) {
            break;
          }
        }

        // const url = `${YOUTUBE_URL}?id=${VIDEO_ID}&key=${YOUTUBE_KEY}&part=snippet,contentDetails,statistics,status`;

        // await fetch(url)
        //   .then(response => response.json())
        //   .then(data => {
        //     let schemaVideo: any = {
        //       '@context': 'https://schema.org',
        //       '@type': 'VideoObject',
        //       name: data?.items[0]?.snippet?.title,
        //       description: data?.items[0]?.snippet?.description,
        //       thumbnailUrl: data?.items[0]?.snippet?.thumbnails?.maxres?.url,
        //       uploadDate: data?.items[0]?.snippet?.publishedAt,
        //       duration: data?.items[0]?.contentDetails.duration,
        //       contentUrl: doc.getElementsByTagName('abc')[i]?.attributes[0]?.value,
        //       embedUrl: doc.getElementsByTagName('abc')[i]?.attributes[0]?.value
        //     };

        //     // setSchemaVideoArr([...schemaVideoArr, schemaVideo])
        //     schemaVideoArr.push(schemaVideo);

        // const divDom = document.createElement('div');
        // divDom.setAttribute('class', 'videoContent');
        // const imgDom = document.createElement('img');
        // imgDom.src = data?.items[0]?.snippet?.thumbnails?.maxres?.url;
        // const spanDom = document.createElement('span');
        // spanDom.setAttribute('class', 'videoPlayer');
        // spanDom.setAttribute('id', videoUrl);
        // spanDom.textContent = '▶';
        // divDom.appendChild(imgDom);
        // divDom.appendChild(spanDom);

        const iframe = document.createElement('iframe');
        iframe.setAttribute('src', `${videoUrl}?autoplay=1`);
        iframe.setAttribute('frameborder', '0');
        iframe.setAttribute(
          'allow',
          'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share',
        );
        iframe.setAttribute('allowfullscreen', '');
        iframe.setAttribute('sandbox', '');

        doc?.getElementsByTagName('abc')[i]?.parentNode?.appendChild(iframe);
        doc?.getElementsByTagName('abc')[i]?.parentNode?.removeChild(doc.getElementsByTagName('abc')[i]);

        const iframeStr = serializer.serializeToString(doc);
        content.rich_text_editor.rich_text_editor = iframeStr;
        // })
        // .catch(error => console.error(error));
      }
    }
    setPageContent(copyPageData);
    setSchemaVideoArr([...schemaVideoArr]);
  };

  const extractPlainText = (html: any) => {
    const $ = cheerio.load(html);

    $('h2').each((index, element) => {
      const $h2 = $(element);
      const id = $h2.text();
      $h2.attr('id', id.replace(/\s/g, ''));
    });

    const updatedHtml = $.html();

    return updatedHtml;
  };

  const handleImgInLink = (ElementStr: string) => {
    const $ = cheerio.load(ElementStr);

    $('a').each((index, element) => {
      const img = $(element).find('img');
      if (img.length > 0) {
        $(element).addClass('imgIn');
      }
    });

    const res = $.html();
    var modifiedStr = extractPlainText(res);

    return modifiedStr;
  };

  useEffect(() => {
    setTimeout(() => {
      const supTags = document.querySelectorAll('sup');
      supTags.forEach(supTag => {
        supTag.addEventListener('click', function () {
          const remarkEle = document.querySelector('#section_four');
          if (remarkEle) {
            const distanceToTop = remarkEle?.getBoundingClientRect().top as number;

            window.scrollBy({
              top: distanceToTop - 185,
              behavior: 'smooth',
            });
            setRemarkCollActiveKey(['1']);
          }
        });
      });
    }, 0);
  }, [pageContent, collActiveKeys]);

  useEffect(() => {
    const domArr: any = document.getElementsByClassName('videoPlayer');

    for (let dom of domArr) {
      dom.addEventListener('click', (e: any) => {
        const iframe = document.createElement('iframe');
        iframe.setAttribute('src', e.target.id);
        iframe.setAttribute('frameborder', '0');
        iframe.setAttribute('allow', 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture');
        iframe.setAttribute('allowfullscreen', 'true');

        let sibling = dom.previousSibling;
        while (sibling) {
          if (sibling.nodeType === 1) {
            sibling.parentNode.removeChild(sibling);
          }
          sibling = sibling.previousSibling;
        }

        if (e.target.parentNode) {
          e.target.parentNode.replaceChild(iframe, e.target);
        }
      });
    }
  }, [copyPageData]);

  useEffect(() => {
    const faqIndexList = questionList.map((item: any, index: number) => {
      return `${index}`;
    });
    setCollActiveKeys(faqIndexList);
  }, []);

  useEffect(() => {
    genVideoInfo(), setOriginUrl(window.location.origin);
  }, []);

  // seo
  const transLateTime = (dateStr: string) => {
    return new Date(dateStr).toISOString();
  };

  const getSeoParams = (p1: string, p2: string) => {
    if (!!p1) {
      return p1;
    } else if (!!p2) {
      return p2;
    } else {
      return '';
    }
  };

  const pathname = usePathname();
  const searchParams = useSearchParams();
  const seoUrl = `${pathname}`;

  return (
    <div className={styles.article_wrapper}>
      <h1 className={styles.article_title}>{articleData?.blog_snippet?.blog_title}</h1>
      {/* date,time, rate*/}
      <div className={styles.time_wrapper}>
        <div className={styles.date}>
          <Image src="/blog/icon/Calendar.svg" className={styles.calendar_icon} alt={''} width={12} height={12} />
          <span>{dayJs(articleData?.blog_snippet?.last_update_date).format(t('fomat_YYMMDD'))}</span>
        </div>
        <div className={styles.read_time}>
          <Image
            src="/blog/icon/ReadTime.svg"
            className={styles.read_time_icon}
            alt={''}
            width={14.67}
            height={14.67}
          />
          <span>
            {articleData.blog_snippet?.estimated_read_time_displayed
              ? `${articleData.blog_snippet?.estimated_read_time_displayed}分鐘閱讀`
              : t('read_time')}
          </span>
        </div>
        <div className={styles.rate}>
          <Image src="/blog/icon/Star.svg" className={styles.star_icon} alt={''} width={13} height={12} />
          <span>{ratingScore.toFixed(1)}</span>
        </div>
      </div>
      {/* banner */}
      {articleData.blog_snippet?.banner_desktop?.url && (
        <NextImage
          loading="eager"
          className={styles.banner}
          alt={articleData.blog_snippet?.banner_alt_tag || ''}
          width={1000}
          height={380}
          src={articleData.blog_snippet?.banner_desktop?.url || placeholder}
        />
      )}

      <RichText pageData={Object.keys(pageContent).length === 0 ? articleData : pageContent} language={language} />

      {questionList.length > 0 && (
        <div className={styles.question_wrapper}>
          <h4 className={styles.question_title}>{t('frequently_question')}</h4>
          <Collapse
            bordered={false}
            expandIconPosition={'end'}
            expandIcon={({ isActive }) => (
              <Image
                src="/blog/icon/PanelIcon.svg"
                className={classNames(styles.expand_icon, isActive ? styles.expand_active_icon : '')}
                alt={''}
                width={24}
                height={24}
              />
            )}
            activeKey={collActiveKeys}
            onChange={res => handleQuestionItem(res)}
            items={questionList.map((item, index) => {
              return {
                key: `${index}`,
                label: item.title,
                children: (
                  <div
                    key={index}
                    id={'rich_text_wrapper'}
                    dangerouslySetInnerHTML={{
                      __html: handleRichText(handleImgInLink(item.answer)),
                    }}
                  ></div>
                ),
              };
            })}
          ></Collapse>
        </div>
      )}

      {remarkList.length > 0 && (
        <div className={styles.remark_wrapper} id={'section_four'}>
          <Collapse
            accordion
            bordered={false}
            expandIconPosition={'end'}
            activeKey={remarkCollActiveKey}
            onChange={(res: any) => {
              setRemarkCollActiveKey(res);
            }}
            expandIcon={({ isActive }) => (
              <Image
                src="/blog/icon/PanelIcon.svg"
                className={classNames(styles.expand_icon, isActive ? styles.expand_active_icon : '')}
                alt={''}
                width={24}
                height={24}
              />
            )}
            items={[
              {
                key: '1',
                label: <div className={styles.remark_title}>{t('remark_title')}</div>,
                children: (
                  <>
                    {remarkList.map((item, index) => {
                      return (
                        <div
                          key={index}
                          id={'remarks'}
                          dangerouslySetInnerHTML={{
                            __html: handleRichText(item),
                          }}
                        ></div>
                      );
                    })}
                  </>
                ),
              },
            ]}
          ></Collapse>
        </div>
      )}

      <Head>
        <>
          {questionList.length && (
            <script
              {...jsonLdScriptProps<FAQPage>({
                '@context': 'https://schema.org',
                '@type': 'FAQPage',
                mainEntity: questionList.map((item: any) => {
                  return {
                    '@type': 'Question',
                    name: item.title,
                    acceptedAnswer: {
                      '@type': 'Answer',
                      text: item.answer,
                    },
                  };
                }),
              })}
            />
          )}
          <script
            {...jsonLdScriptProps<any>({
              '@context': 'https://schema.org',
              '@type': 'BlogPosting',
              mainEntityOfPage: {
                '@type': 'WebPage',
                '@id': seoUrl,
              },
              logo: headerData.logo?.logo_image?.url?.replace('images.contentstack.io', 'www.fwd.com.hk/files') || '',
              headline: getSeoParams(articleData.webpage_settings?.seo?.meta_title, articleData.title),
              description: getSeoParams(
                articleData.webpage_settings?.seo?.meta_description,
                articleData.blog_snippet?.description,
              ),
              image: articleData.blog_snippet?.banner_mobile?.url?.replace(
                'images.contentstack.io',
                'www.fwd.com.hk/files',
              ),
              author: {
                '@type': 'Person',
                name: articleData.blog_snippet?.author[0].title,
                url: articleData?.blog_snippet?.author[0]
                  ? `${originUrl}/${language}/blog/author${articleData.blog_snippet.author[0].author_url}`
                  : '',
              },
              datePublished: articleData.blog_snippet?.post_date
                ? transLateTime(articleData.blog_snippet?.post_date)
                : '',
              dateModified: articleData.blog_snippet?.last_update_date
                ? transLateTime(articleData.blog_snippet?.last_update_date)
                : '',
              publisher: {
                '@type': 'Organization',
                name: 'FWD HK',
                logo: {
                  '@type': 'ImageObject',
                  url: articleData.blog_snippet?.banner_desktop?.url?.replace(
                    'images.contentstack.io',
                    'www.fwd.com.hk/files',
                  )
                    ? articleData.blog_snippet?.banner_desktop?.url?.replace(
                        'images.contentstack.io',
                        'www.fwd.com.hk/files',
                      )
                    : '',
                },
              },
            })}
          />
          {schemaVideoArr?.map((item: any) => (
            <script key={Math.random()} {...jsonLdScriptProps<VideoObject>(item)} />
          ))}
        </>
      </Head>
    </div>
  );
};

export default IntroDetail;
